
import { createRoot, type Root } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { initSentry } from './sentry';
import App from './App';
import 'react-glasskit/css/tokens.css';
import './style.css';
// biome-ignore lint/correctness/noUnresolvedImports: vite-plugin-pwa exposes this virtual module during the Vite build.
import { registerSW } from 'virtual:pwa-register';
import { logger } from './utils/logger';
import { setPwaReady } from './pwa-bridge';
import type { PermissionValue } from './features/auth/types';
import { seedE2EAuthProfileForTesting } from './features/auth/auth-service';
import {
    getProjectWriteOutboxEntry,
    requeueProjectWrite,
    storeProjectWriteIntent,
} from './features/sync/project-write-outbox';
import { useAppStore } from './store/useAppStore';

type E2EProjectStateSeed = {
    user: {
        uid: string;
        email: string;
        displayName: string;
    };
    orgId: string;
    projectId: string;
    title: string;
    permissions: PermissionValue[];
    joinedAt: number;
    cloudEnabled?: boolean;
};

// Initialize Sentry before anything else
initSentry();

// ── PWA Registration ──────────────────────────────────────
// Bridge state lives in pwa-bridge.ts to avoid circular dep with App tree.
const SERVICE_WORKER_UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;
let serviceWorkerUpdateChecksStarted = false;

function requestServiceWorkerUpdate(registration: ServiceWorkerRegistration | undefined): void {
    if (!registration) {
        return;
    }

    void registration.update().catch((error: unknown) => {
        logger.pwa('Service worker update check failed', error);
    });
}

function startServiceWorkerUpdateChecks(registration: ServiceWorkerRegistration | undefined): void {
    if (!registration || serviceWorkerUpdateChecksStarted || typeof window === 'undefined') {
        return;
    }

    serviceWorkerUpdateChecksStarted = true;
    requestServiceWorkerUpdate(registration);

    window.setInterval(() => {
        if (document.visibilityState === 'visible') {
            requestServiceWorkerUpdate(registration);
        }
    }, SERVICE_WORKER_UPDATE_CHECK_INTERVAL_MS);

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            requestServiceWorkerUpdate(registration);
        }
    });
}

const updateSW = registerSW({
    immediate: true,
    onRegisteredSW(_swScriptUrl, registration) {
        startServiceWorkerUpdateChecks(registration);
    },
    onNeedRefresh() {
        setPwaReady(updateSW);
        logger.pwa('New content available — waiting for user action.');
    },
    onOfflineReady() {
        logger.pwa('App ready to work offline.');
    },
});

interface ReactRootElement extends HTMLElement {
    __reactRoot?: Root;
}

const renderApp = (root: Root): void => {
    root.render(
        <Sentry.ErrorBoundary showDialog fallback={<p>Something went wrong. Please refresh.</p>}>
            <App />
        </Sentry.ErrorBoundary>
    );
};

// Mount React App. Cache the root so Vite HMR reuses it instead of
// calling createRoot() twice, which tears down context providers.
const container = document.getElementById('root');
if (container) {
    const rootContainer = container as ReactRootElement;
    const root = rootContainer.__reactRoot ?? createRoot(rootContainer);
    rootContainer.__reactRoot = root;
    renderApp(root);
    logger.log('React application initialized');
} else {
    logger.error('Root element not found');
}

if (import.meta.env.VITE_E2E === 'true' && typeof window !== 'undefined') {
    window.__ON_BOOK_E2E_PROJECT_WRITES__ = {
        enqueueProjectWrite: storeProjectWriteIntent,
        getProjectWriteOutboxEntry,
        requeueProjectWrite,
        storeProjectWriteIntent,
    };
    window.__ON_BOOK_E2E_APP_STATE__ = {
        seedProjectState: (seed: E2EProjectStateSeed) => {
            seedE2EAuthProfileForTesting(seed.user);

            const member = {
                userId: seed.user.uid,
                roleId: 'owner',
                status: 'active' as const,
                joinedAt: seed.joinedAt,
            };
            const ownerRole = {
                id: 'owner',
                name: 'Owner',
                permissions: seed.permissions,
                isBuiltIn: true,
                description: 'Full access to all project features',
            };

            useAppStore.setState((state) => ({
                auth: {
                    ...state.auth,
                    user: seed.user,
                    isAuthenticated: true,
                    isAuthModalOpen: false,
                },
                cloud: {
                    ...state.cloud,
                    enabled: seed.cloudEnabled ?? true,
                    orgId: seed.orgId,
                    projectId: seed.projectId,
                    role: 'owner',
                    member,
                    currentProjectId: seed.projectId,
                    lastSync: null,
                    scriptTextAccess: true,
                    isBeta: true,
                },
                admin: {
                    ...state.admin,
                    members: [member],
                    roles: [ownerRole],
                },
                projectMeta: {
                    ...state.projectMeta,
                    title: seed.title,
                },
                ui: {
                    ...state.ui,
                    activeWorkspace: 'stage-management',
                    activeTab: 'show-structure',
                    sidebarCollapsed: false,
                },
            }));
        },
    };
}
